import { type ComponentType, type CSSProperties, memo } from 'react';
import type { LucideProps } from 'lucide-react';
import {
  AlarmClock, Aperture, Atom, AudioLines, AudioWaveform,
  Banknote, BarChart3, Binary, Blend, BookMarked, BookOpen,
  Braces, Brain, Brush, Bug, CalendarDays, Camera,
  CircleAlert, CircleCheck, CircleDashed, CircleDot, Clapperboard,
  ClipboardList, Clock, CloudRain, Compass, Crown,
  Database, Dice5, Drama, Eye, EyeOff,
  FileCode, FileDiff, FileText, FileUp, Flag, FlaskConical, Flower, Focus,
  Gamepad2, Gauge, Gift, Globe, GraduationCap,
  HardDrive, Hash, Headphones, Heart, Home, Hourglass,
  Image, ImageUp, KeyRound, Keyboard, Layers, LayoutGrid,
  Library, Lightbulb, Link, Lock, Mail, MailCheck, Map as LucideMap, Maximize,
  MessageSquare, Metronome, Mic, Monitor, Moon, Music,
  NotebookPen, Orbit, Package, Paintbrush, Palette, PenLine, PenTool, Pencil,
  Percent, Phone, Pilcrow, Pipette, QrCode, Quote,
  Rainbow, Ratio, Regex, Rocket, Ruler,
  Sandwich, Scale, Scan, ScrollText, Search, Send, Settings,
  Share2, Shield, Shrink, SlidersHorizontal, Smartphone,
  Sparkles, Split, Square, SquareAsterisk, SquareTerminal,
  Stars, Sun, TextSearch, Thermometer, Timer, Trash2, Trophy, Type,
  Users, Video, Volume2, Vote, WandSparkles, Waves, Wind, Wrench, Zap,
  ArrowDown, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUp, ArrowUpRight, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  Copy, Download, Info, Minus, Pause, Play as LucidePlay, Plus, MicOff, RotateCcw,
  Shuffle, X,
  FileSpreadsheet, FileArchive, Film, Code, Clipboard, Paperclip,
  Presentation, FileJson, RefreshCw,
  Target, CircleX,
  Loader2, File, PartyPopper, RotateCw, Disc3, MailPlus,
  Mountain, Triangle, Car, List, ListChecks, Tv,
  RotateCcwSquare, RotateCwSquare, FlipHorizontal2, FlipVertical2,
  Undo2, Redo2, BringToFront, SendToBack, StickyNote, Minimize, SkipBack, SkipForward,
  Bold, Italic, Heading, ListOrdered, Table, Printer,
  Pin, Delete, Space, ZoomIn, ZoomOut,
} from 'lucide-react';

/**
 * Static registry of Lucide icon name → component.
 * Only icons listed here are included in the bundle (proper tree-shaking).
 */
const ICON_MAP = {
  // Module library icons
  'sparkles': Sparkles,
  'music': Music,
  'camera': Camera,
  'zap': Zap,
  'vote': Vote,
  'calendar-days': CalendarDays,
  'calendar': CalendarDays,
  'drama': Drama,
  'search': Search,
  'palette': Palette,
  'lightbulb': Lightbulb,
  'bar-chart-3': BarChart3,
  'headphones': Headphones,
  'pen-line': PenLine,
  'alarm-clock': AlarmClock,
  'waves': Waves,
  'clapperboard': Clapperboard,
  'package': Package,
  'key-round': KeyRound,
  'ruler': Ruler,
  'circle-dot': CircleDot,
  'book-open': BookOpen,
  'globe': Globe,
  'banknote': Banknote,
  'video': Video,
  'keyboard': Keyboard,
  'brush': Brush,
  'rainbow': Rainbow,
  'share-2': Share2,
  'mic': Mic,
  'bug': Bug,
  'monitor': Monitor,
  'file-text': FileText,
  'home': Home,
  'braces': Braces,
  'regex': Regex,
  'hash': Hash,
  'binary': Binary,
  'percent': Percent,
  'dice-5': Dice5,
  'timer': Timer,
  'clock': Clock,
  'hourglass': Hourglass,
  'send': Send,
  'link': Link,
  'eye': Eye,
  'trophy': Trophy,
  'users': Users,
  'gift': Gift,
  'graduation-cap': GraduationCap,
  'sliders-horizontal': SlidersHorizontal,
  'image': Image,
  'type': Type,
  'square-asterisk': SquareAsterisk,
  'pencil': Pencil,

  'gamepad-2': Gamepad2,

  'brain': Brain,
  'message-square': MessageSquare,
  'file-code': FileCode,
  'layers': Layers,
  'scan': Scan,

  'crown': Crown,
  'library': Library,
  'flower': Flower,
  'sun': Sun,
  'aperture': Aperture,
  'sandwich': Sandwich,
  'shield': Shield,
  'lock': Lock,
  'wrench': Wrench,
  'rocket': Rocket,
  'database': Database,
  'atom': Atom,
  'paintbrush': Paintbrush,
  'settings': Settings,
  'flag': Flag,
  'mail': Mail,
  'clipboard-list': ClipboardList,
  'smartphone': Smartphone,
  'compass': Compass,
  'orbit': Orbit,
  'mountain': Mountain,
  'triangle': Triangle,
  'car': Car,
  'shrink': Shrink,
  'map': LucideMap,
  'text-search': TextSearch,
  'list': List,
  'list-checks': ListChecks,
  'tv': Tv,
  'layout-grid': LayoutGrid,
  'hard-drive': HardDrive,
  'gauge': Gauge,
  'circle-dashed': CircleDashed,
  'maximize': Maximize,
  'book-marked': BookMarked,
  'metronome': Metronome,
  'scale': Scale,

  // Added in icon accuracy pass
  'scroll-text': ScrollText,
  'eye-off': EyeOff,
  'audio-lines': AudioLines,
  'pipette': Pipette,
  'focus': Focus,
  'audio-waveform': AudioWaveform,
  'qr-code': QrCode,
  'ratio': Ratio,
  'wand-sparkles': WandSparkles,
  'volume-2': Volume2,
  'pilcrow': Pilcrow,
  'split': Split,
  'notebook-pen': NotebookPen,
  'file-diff': FileDiff,
  'image-up': ImageUp,
  'file-up': FileUp,
  'quote': Quote,
  'pen-tool': PenTool,
  'square-terminal': SquareTerminal,
  'mail-check': MailCheck,
  'circle-alert': CircleAlert,
  'circle-check': CircleCheck,
  'phone': Phone,
  'blend': Blend,

  // Unit categories & other
  'thermometer': Thermometer,
  'flask-conical': FlaskConical,
  'square': Square,
  'wind': Wind,
  'moon': Moon,
  'cloud-rain': CloudRain,
  'heart': Heart,
  'trash-2': Trash2,
  'stars': Stars,

  // Extended UI icons
  'file-spreadsheet': FileSpreadsheet,
  'file-archive': FileArchive,
  'film': Film,
  'code': Code,
  'clipboard': Clipboard,
  'paperclip': Paperclip,
  'presentation': Presentation,
  'file-json': FileJson,
  'refresh-cw': RefreshCw,
  'target': Target,
  'circle-x': CircleX,
  'loader-2': Loader2,
  'file': File,
  'party-popper': PartyPopper,
  'rotate-cw': RotateCw,
  'disc-3': Disc3,
  'mail-plus': MailPlus,

  // UI chrome
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-left-right': ArrowLeftRight,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-up-right': ArrowUpRight,
  'check': Check,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'copy': Copy,
  'download': Download,
  'info': Info,
  'minus': Minus,
  'pause': Pause,
  'play': LucidePlay,
  'plus': Plus,
  'mic-off': MicOff,
  'rotate-ccw': RotateCcw,
  'shuffle': Shuffle,
  'x': X,
  // Turning and mirroring an image (Photo Editor)
  'rotate-ccw-square': RotateCcwSquare,
  'rotate-cw-square': RotateCwSquare,
  'flip-horizontal-2': FlipHorizontal2,
  'flip-vertical-2': FlipVertical2,
  'undo-2': Undo2,
  'redo-2': Redo2,
  'bring-to-front': BringToFront,
  'send-to-back': SendToBack,
  'sticky-note': StickyNote,
  // Leaving full screen, beside 'maximize' (Selfie Light)
  'minimize': Minimize,
  // Skipping to what comes next (Pomodoro's next phase), or back (Speed Reader's sentences)
  'skip-forward': SkipForward,
  'skip-back': SkipBack,
  // Markdown's formatting toolbar and printing
  'bold': Bold,
  'italic': Italic,
  'heading': Heading,
  'list-ordered': ListOrdered,
  'table': Table,
  'printer': Printer,
  // Font Preview's pins; Fontren's decoder keys (backspace and space)
  'pin': Pin,
  'delete': Delete,
  'space': Space,
  // Photography's viewer: zooming into a photo and back out
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
} satisfies Record<string, ComponentType<LucideProps>>;

/** A registered icon name. Library cards and FlagshipLayout take this, so an unknown icon fails tsc. */
export type IconName = keyof typeof ICON_MAP;

const warnedNames = new Set<string>();

function warnUnknownIcon(name: string) {
  if (!import.meta.env.DEV || warnedNames.has(name)) return;
  warnedNames.add(name);
  console.warn(`ModuleIcon: no icon named "${name}". Register it in ICON_MAP (src/components/ModuleIcon.tsx).`);
}

export interface ModuleIconProps {
  name: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
  strokeWidth?: number;
  /** Accessible label — omit for decorative icons */
  label?: string;
}

/**
 * Renders a Lucide icon by its kebab-case name.
 * Renders nothing for a name missing from ICON_MAP, with a console warning in dev.
 */
export const ModuleIcon = memo(function ModuleIcon({
  name,
  size = 24,
  className,
  style,
  strokeWidth = 1.5,
  label,
}: ModuleIconProps) {
  // hasOwn keeps inherited keys such as "constructor" from resolving.
  const Icon = Object.hasOwn(ICON_MAP, name) ? ICON_MAP[name as IconName] : undefined;

  if (!Icon) {
    warnUnknownIcon(name);
    return null;
  }

  return (
    <Icon
      size={size}
      className={className}
      style={style}
      strokeWidth={strokeWidth}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
});

export default ModuleIcon;
